import {Injectable} from "@angular/core";
import {Actions, createEffect, ofType} from "@ngrx/effects";
import {WorkingGroupsActions} from "../actions";
import {catchError, map, mergeMap} from "rxjs";
import {ToastrService} from "ngx-toastr";
import {
  ApiError,
  ConfigurationService,
  WorkingGroupPostsService,
  WorkingGroupRequestsService,
  WorkingGroupsService,
  WorkingGroupStatusEnum
} from "@esa/api";
import {Router} from "@angular/router";
import {WorkingGroupRequestStatusEnum} from "@esa/api/models/WorkingGroupRequestStatusEnum";
import {ApiErrorHandlerService} from "../../services";

@Injectable()
export class WorkingGroupsEffects {

  getWorkingGroups$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkingGroupsActions.getWorkingGroups),
      mergeMap((action) => this._workingGroupsService.getApiWorkingGroup(
        action.pageNumber,
        action.pageSize,
        null,
        action.groupStatus,
        null,
        null,
        null,
      ).pipe(
        map(response => WorkingGroupsActions.getWorkingGroupsSuccess({
          workingGroups: response.data,
          workingGroupsStats: {
            totalGroups: response.totalCount,
            totalMembers: response.totalMembers,
            activeMembers: response.totalActiveMembers
          }
        })),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, WorkingGroupsActions.getWorkingGroupsError());
        })
      ))
    );
  });

  createWorkingGroup$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkingGroupsActions.createWorkingGroup),
      mergeMap(({ workingGroup }) => this._workingGroupsService.postApiWorkingGroup(workingGroup).pipe(
        map(response => {
          this.toastrService.success('Working group created successfully');
          this.router.navigateByUrl('working-groups');
          return WorkingGroupsActions.createWorkingGroupSuccess();
        }),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, WorkingGroupsActions.createWorkingGroupError());
        })
      ))
    );
  });

  getWorkingGroupById$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkingGroupsActions.getWorkingGroupById),
      mergeMap(({ id }) => this._workingGroupsService.getApiWorkingGroup1(id).pipe(
        map(selectedWorkingGroup => WorkingGroupsActions.getWorkingGroupByIdSuccess({ selectedWorkingGroup })),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, WorkingGroupsActions.getWorkingGroupByIdError());
        })
      ))
    );
  });

  getWorkingGroupTypes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkingGroupsActions.getWorkingGroupTypes),
      mergeMap(({}) => this._configurationService.getApiEnums('WorkingGroupTypeEnum').pipe(
        map(response => WorkingGroupsActions.getWorkingGroupTypesSuccess({ workingGroupTypes: response[0].enumValues })),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, WorkingGroupsActions.getWorkingGroupTypesError());
        })
      ))
    );
  });

  updateWorkingGroup$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkingGroupsActions.updateWorkingGroup),
      mergeMap(({ workingGroup }) => this._workingGroupsService.putApiWorkingGroup(workingGroup).pipe(
        map(response => {
          this.toastrService.success('Working group updated successfully');
          this.router.navigateByUrl('working-groups');
          return WorkingGroupsActions.updateWorkingGroupSuccess();
        }),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, WorkingGroupsActions.updateWorkingGroupError());
        })
      ))
    );
  });

  archiveWorkingGroup$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkingGroupsActions.archiveWorkingGroup),
      mergeMap(({ id }) => this._workingGroupsService.putApiWorkingGroupArchive({ id }).pipe(
        map(() => {
          this.toastrService.success('Working group archived successfully');
          return WorkingGroupsActions.archiveWorkingGroupSuccess();
        }),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, WorkingGroupsActions.archiveWorkingGroupError());
        })
      ))
    );
  });

  unarchiveWorkingGroup$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkingGroupsActions.unarchiveWorkingGroup),
      mergeMap(({ id }) => this._workingGroupsService.putApiWorkingGroupUnarchive({ id }).pipe(
        map(() => {
          this.toastrService.success('Working group unarchived successfully');
          return WorkingGroupsActions.unarchiveWorkingGroupSuccess();
        }),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, WorkingGroupsActions.unarchiveWorkingGroupError());
        })
      ))
    );
  });

  loadWorkingGroupsOnArchive$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkingGroupsActions.archiveWorkingGroupSuccess),
      map(() => WorkingGroupsActions.getWorkingGroups({ pageNumber: 1, pageSize: 10, groupStatus: 'Active' as  WorkingGroupStatusEnum }))
    );
  });

  loadWorkingGroupsOnUnarchive$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkingGroupsActions.unarchiveWorkingGroupSuccess),
      map(() => WorkingGroupsActions.getWorkingGroups({ pageNumber: 1, pageSize: 10, groupStatus: 'Archived' as  WorkingGroupStatusEnum }))
    );
  });

  getSelectedWorkingGroupsPosts$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkingGroupsActions.getSelectedWorkingGroupPosts),
      mergeMap((action) => this._workingGroupPostsService.getApiWorkingGroupPost(
        action.id,
        action.search,
        action.pageNumber,
        action.pageSize
      ).pipe(
        map(response => WorkingGroupsActions.getSelectedWorkingGroupPostsSuccess({ selectedWorkingGroupPosts: response.data, selectedWorkingGroupPostsCount: response.totalCount })),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, WorkingGroupsActions.getSelectedWorkingGroupPostsError());
        })
      ))
    );
  });

  createWorkingGroupPost$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkingGroupsActions.createWorkingGroupPost),
      mergeMap((action) => this._workingGroupPostsService.postApiWorkingGroupPost(action.post).pipe(
        map(response => {
          this.toastrService.success('Working group post created successfully');
          return WorkingGroupsActions.createWorkingGroupPostSuccess({
            post: action.post,
            pageNumber: action.pageNumber,
            pageSize: action.pageSize
          });
        }),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, WorkingGroupsActions.createWorkingGroupPostError());
        })
      ))
    );
  });

  loadWorkingGroupPostsOnCreate$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkingGroupsActions.createWorkingGroupPostSuccess),
      map((action) => WorkingGroupsActions.getSelectedWorkingGroupPosts({
        id: action.post.workingGroupId,
        search: '',
        pageNumber: action.pageNumber,
        pageSize: action.pageSize
      }))
    );
  });

  createWorkingGroupAccessRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkingGroupsActions.createWorkingGroupRequest),
      mergeMap((action) => this._workingGroupRequestsService.postApiWorkingGroupRequest({
        workingGroupId: action.id,
        reason: action.reason
      }).pipe(
        map(response => {
          this.toastrService.success('Working group request created successfully');
          return WorkingGroupsActions.createWorkingGroupRequestSuccess();
        }),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, WorkingGroupsActions.createWorkingGroupRequestError());
        })
      ))
    );
  });

  loadWorkingGroupsOnCreateRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkingGroupsActions.createWorkingGroupRequestSuccess),
      map(() => WorkingGroupsActions.getWorkingGroups({ pageNumber: 1, pageSize: 10, groupStatus: 'Active' as  WorkingGroupStatusEnum }))
    );
  });

  getSelectedWorkingGroupRequests$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkingGroupsActions.getSelectedWorkingGroupRequests),
      mergeMap((action) => this._workingGroupRequestsService.getApiWorkingGroupRequest(action.id, action.requestStatus).pipe(
        map(selectedWorkingGroupRequests => WorkingGroupsActions.getSelectedWorkingGroupRequestsSuccess({ selectedWorkingGroupRequests })),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, WorkingGroupsActions.getSelectedWorkingGroupRequestsError());
        })
      ))
    );
  });

  approveSelectedWorkingGroupRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkingGroupsActions.approveSelectedWorkingGroupRequest),
      mergeMap((action) => this._workingGroupRequestsService.putApiWorkingGroupRequestApprove({
        id: action.id,
      }).pipe(
        map(response => {
          this.toastrService.success('Working group request approved successfully');
          return WorkingGroupsActions.approveSelectedWorkingGroupRequestSuccess({ workingGroupId: action.workingGroupId });
        }),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, WorkingGroupsActions.approveSelectedWorkingGroupRequestError());
        })
      ))
    );
  });

  rejectSelectedWorkingGroupRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkingGroupsActions.rejectSelectedWorkingGroupRequest),
      mergeMap((action) => this._workingGroupRequestsService.putApiWorkingGroupRequestReject({
        id: action.id,
        rejectReason: action.reason
      }).pipe(
        map(response => {
          this.toastrService.success('Working group request rejected successfully');
          return WorkingGroupsActions.rejectSelectedWorkingGroupRequestSuccess({ workingGroupId: action.workingGroupId });
        }),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, WorkingGroupsActions.rejectSelectedWorkingGroupRequestError());
        })
      ))
    );
  });

  loadWorkingGroupsOnApproveRejectRequest$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkingGroupsActions.approveSelectedWorkingGroupRequestSuccess, WorkingGroupsActions.rejectSelectedWorkingGroupRequestSuccess),
      map((action) => WorkingGroupsActions.getSelectedWorkingGroupRequests({
        id: action.workingGroupId,
        requestStatus: 'Pending' as WorkingGroupRequestStatusEnum
      }))
    );
  });

  approveWorkingSelectedGroup$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkingGroupsActions.approveSelectedWorkingGroup),
      mergeMap((action) => this._workingGroupsService.putApiWorkingGroupApprove({
        id: action.id,
      }).pipe(
        map(response => {
          this.toastrService.success('Working group approved successfully');
          return WorkingGroupsActions.approveSelectedWorkingGroupSuccess();
        }),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, WorkingGroupsActions.approveSelectedWorkingGroupError());
        })
      ))
    );
  });

  rejectWorkingSelectedGroup$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkingGroupsActions.rejectSelectedWorkingGroup),
      mergeMap((action) => this._workingGroupsService.putApiWorkingGroupReject({
        id: action.id,
        rejectReason: action.reason
      }).pipe(
        map(response => {
          this.toastrService.success('Working group rejected successfully');
          return WorkingGroupsActions.rejectSelectedWorkingGroupSuccess();
        }),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, WorkingGroupsActions.rejectSelectedWorkingGroupError());
        })
      ))
    );
  });

  loadWorkingGroupsOnApproveReject$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkingGroupsActions.approveSelectedWorkingGroupSuccess, WorkingGroupsActions.rejectSelectedWorkingGroupSuccess),
      map((action) => WorkingGroupsActions.getWorkingGroups({
        pageNumber: 1,
        pageSize: 10,
        groupStatus: 'Pending' as WorkingGroupStatusEnum
      }))
    );
  });

  assignGroupAdmin$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkingGroupsActions.assignGroupAdmin),
      mergeMap((action) => this._workingGroupsService.putApiWorkingGroupAdmin({
        workingGroupId: action.workingGroupId,
        userId: action.userId
      }).pipe(
        map(response => {
          this.toastrService.success('Working group admin assigned successfully.');
          return WorkingGroupsActions.assignGroupAdminSuccess({
            pageNumber: action.pageNumber,
            pageSize: action.pageSize
          });
        }),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, WorkingGroupsActions.assignGroupAdminError());
        })
      ))
    );
  });

  loadWorkingGroupsOnAssignAdmin$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkingGroupsActions.assignGroupAdminSuccess),
      map((action) => WorkingGroupsActions.getWorkingGroups({
        pageNumber: action.pageNumber,
        pageSize: action.pageSize,
        groupStatus: 'Active' as WorkingGroupStatusEnum
      }))
    );
  });

  getSelectedWorkingGroupsPostDocuments$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(WorkingGroupsActions.getWorkingGroupPostDocuments),
      mergeMap((action) => this._workingGroupPostsService.getApiWorkingGroupPostDocuments(action.workingGroupId, action.pageNumber, action.pageSize).pipe(
        map(response => WorkingGroupsActions.getWorkingGroupPostDocumentsSuccess({
          selectedWorkingGroupPostDocuments: response.data,
          totalDocuments: response.totalCount
        })),
        catchError((error: ApiError) => {
          return this.apiErrorHandlerService.handleError(error, WorkingGroupsActions.getWorkingGroupPostDocumentsError());
        })
      ))
    );
  });

  constructor(
    private actions$: Actions,
    private _workingGroupsService: WorkingGroupsService,
    private _configurationService: ConfigurationService,
    private _workingGroupPostsService: WorkingGroupPostsService,
    private _workingGroupRequestsService: WorkingGroupRequestsService,
    private toastrService: ToastrService,
    private router: Router,
    private apiErrorHandlerService: ApiErrorHandlerService
  ) {}
}
